import { JSX } from "react";
import { ActiveLabel, Bar, BarChart, BarShapeProps, BaseTickContentProps, CartesianGrid, LabelList, Rectangle, ResponsiveContainer, Tooltip, TooltipContentProps, XAxis, YAxis } from "recharts";
import { ValueType } from "recharts/types/component/DefaultTooltipContent";
import { BenchmarkingDashboardDetailsTypeEnum } from "@app/overviews/network-benchmarking-dashboard/dashboard-details";
import { ProvidersByNetworkData } from "@app/overviews/network-benchmarking-dashboard/tiles/shared";
import { colors, tickFormatter, widgetTooltipCursor } from "@app/overviews/network-benchmarking-dashboard/tiles/utility";
import { ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { printNumberAsString } from "@app/shared/waffles";
import { useHoverBoundary } from "@app/shared/hooks/useHoverBoundary";

interface ProvidersByNetworkProps {
  customTooltip: (
    props: TooltipContentProps<ValueType, string | number>,
    titleKey: string,
  ) => JSX.Element;
  customYAxisTick?: (props: BaseTickContentProps) => JSX.Element;
  data: ProvidersByNetworkData[]
  onDashboardDetailsViewClick: (
    drillinOption: BenchmarkingDashboardDetailsTypeEnum,
    clickedCount: number,
    selectedVersionId?: number,
    networkOverlappingCount?: number,
    volumeLabel?: ClaimsActivityVolumeLabelEnum,
  ) => void;
  selectedNetwork: string;
}

const ProvidersByNetwork = (props: ProvidersByNetworkProps): JSX.Element => {
  const { isHovered: isChartHovered, ref: chartRef } = useHoverBoundary<HTMLDivElement>();

  const onProvidersByNetworkClick = (activeLabel: ActiveLabel): void => {
    // only allow to drill into the dashboard details view when
    // clicking in a bar section and the ability to drill into the dashboard is enabled
    if (!!activeLabel) {
      const contractData: ProvidersByNetworkData = props.data.find(x => x.contractId === activeLabel);

      if (!!contractData?.providerCount) {
        props.onDashboardDetailsViewClick(BenchmarkingDashboardDetailsTypeEnum.NetworkDrillin, contractData?.providerCount, contractData?.versionId);
      }
    }
  }

  return (
    <div
      ref={chartRef}
      style={{ height: "300px", width: "100%" }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          barCategoryGap="20%"
          data={props.data}
          layout="vertical"
          margin={{
            bottom: 0,
            left: 0,
            right: 50, // formatting to prevent the number displayed to right of the longest bar from truncating
            top: 20,
          }}
          onClick={(e) => { onProvidersByNetworkClick(e.activeLabel) }}
        >
          <CartesianGrid strokeDasharray="1 0" horizontal={false} />
          <XAxis type="number" dataKey="providerCount" tickLine={false} axisLine={false} tickFormatter={tickFormatter} />
          <YAxis dataKey="contractId" type="category" tickLine={false} tick={props.customYAxisTick} />
          <Tooltip content={(customTooltipProps) => props.customTooltip(customTooltipProps, "contractId")} cursor={widgetTooltipCursor} active={isChartHovered ? undefined : false} />
          <Bar
            dataKey="providerCount"
            shape={(shapeProps: BarShapeProps) => {
              const entry = shapeProps.payload as ProvidersByNetworkData;

              return (
                // custom bar chart colors
                <Rectangle
                  {...shapeProps}
                  id={`benchmarking-dashboard__providers-by-network--contract-${entry.contractId}-count-${entry.providerCount}`}
                  fill={entry.contractId === props.selectedNetwork ? colors.green : colors.blue}
                  cursor="pointer"
                />
              );
            }}
          >
            <LabelList
              formatter={(providerCount: number) => printNumberAsString(providerCount, "0")}
              dataKey="providerCount"
              position="right" // adding the provider count to the side of a bar
              fill="#000000"
              cursor="default"
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

export default ProvidersByNetwork;
